import { Component, Input } from '@angular/core';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { Chart } from 'chart.js';
import { ICostCenterReport, IReport } from '../../../../interfaces/reports';
import { PercentPipe } from '@angular/common';
import * as moment from 'moment';

@Component({
  selector: 'general-view',
  templateUrl: './general-view.component.html',
  styleUrls: ['../../reports.component.scss', './general-view.component.scss'],
  providers: [PercentPipe]
})
export class GeneralViewComponent {

  @Input() reports: IReport[];
  @Input() places: any[] = [];

  @Input() set selectedGroups(groups: string | string[]) {
    this.groups = groups;
    this.currentGroupData = this.getCurrentGroupData(0);
    this.updateDataTable();
  }

  chart;
  groups: string | string[] = 'all';
  currentGroupData: ICostCenterReport;
  columnModes = ColumnMode;
  grownMetricsRows = [];
  grownMetricsColumns = [
    { prop: 'period', name: 'Período', flexGrow: 2 },
    { prop: 'registered', name: 'Cadastrados', flexGrow: 1 },
    { prop: 'adhered', name: 'Aderidos', flexGrow: 1 },
    { prop: 'adhered_registered', name: '% Aderidos - Cadastrados', flexGrow: 1 },
    { prop: 'use', name: 'Usuários Únicos (USO)', flexGrow: 1 },
    { prop: 'use_adhered', name: '% Uso - Aderidos', flexGrow: 1 },
    { prop: 'use_registered', name: '% Uso - Cadastrados', flexGrow: 1 },
    { prop: 'coupons', name: 'Cupons', flexGrow: 0.5 },
  ];

  constructor(private percentPipe: PercentPipe) {}

  updateDataTable() {
    this.grownMetricsRows = this.reports.map((el, index) => {
      const groupReport = this.getCurrentGroupData(index);
      const adhered_registered = this.percentPipe.transform(groupReport.adhered_registered, '1.2-2') || '-';
      const use_registered = this.percentPipe.transform(groupReport.use_registered, '1.2-2') || '-';
      const use_adhered = this.percentPipe.transform(groupReport.use_adhered, '1.2-2') || '-';

      return {
        ...groupReport,
        period: moment.utc(el.date).format('MMM/YY'),
        adhered_registered,
        use_adhered,
        use_registered,
        registered: groupReport.registered || '-',
        adhered: groupReport.adhered || '-',
        use: groupReport.use || '-',
        coupons: groupReport.coupons || '-'
      };
    });

    this.updateChart();
  }

  getCurrentGroupData(index = 0): ICostCenterReport {
    if (this.groups === 'all') {
      const report = this.reports[index].data_by_cost_center.find(el => el.costCenter === 'all') || {};
      return { ...report, launchedEconomy: (report.couponLaunchedEconomy + report.cinema_value) || 0 };
    } else {
      const report = this.reports[index].data_by_cost_center.filter(el => this.groups.includes(el.costCenter));
      return this.sumGroups(report);
    }
  }

  initChart() {
    this.chart = new Chart('canvas', {
      type: 'bar',
      data: {
        labels: this.grownMetricsRows.map(el => el.period).reverse(),
        datasets: []
      },
      options: {
        responsive: true,
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const datasetIndex = tooltipItem.datasetIndex;
              const labelValue = datasetIndex === 3 ? this.percentPipe.transform(tooltipItem.yLabel, '1.2-2') : tooltipItem.yLabel;
              return `${data.datasets[datasetIndex].label}: ${labelValue}`;
            }
          },
        },
        scales: {
          xAxes: [{ gridLines: { color: '#F0F0F0' } }],
          yAxes: [
            {
              id: 'y',
              gridLines: { color: '#F0F0F0' },
              ticks: { fontFamily: 'Roboto' },
              scaleLabel: {
                display: true,
                labelString: 'Cadastrados, Aderidos e Uso único'
              }
            },
            {
              id: 'y2',
              type: 'linear',
              display: true,
              position: 'right',
              gridLines: {
                drawOnChartArea: false, // only want the grid lines for one axis to show up
              },
              ticks: {
                callback: (value) => this.percentPipe.transform(value, '1.0-0')
              },
              scaleLabel: {
                display: true,
                labelString: 'Uso / Aderidos (%)'
              }
            }],
          yAxesRight: {}
        }
      },
    });
  }

  updateChart() {
    if (this.chart) {
      this.chart.data.datasets = [
        {
          type: 'line',
          backgroundColor: 'rgba(208, 0, 111, 0)',
          borderColor: 'rgba(208, 0, 111, 1)',
          borderWidth: 3,
          label: 'Cadastrados',
          data: this.grownMetricsRows.map(el => el.registered).reverse(),
          yAxisID: 'y'
        },
        {
          type: 'line',
          backgroundColor: 'rgba(0, 155, 164, 0)',
          borderColor: 'rgba(0, 155, 164, 1)',
          borderWidth: 3,
          label: 'Aderidos',
          data: this.grownMetricsRows.map(el => el.adhered).reverse(),
          yAxisID: 'y'
        },
        {
          type: 'line',
          backgroundColor: 'rgba(255, 103, 76, 0)',
          borderColor: 'rgba(255, 103, 76, 1)',
          borderWidth: 3,
          label: 'Uso único',
          data: this.grownMetricsRows.map(el => el.use).reverse(),
          yAxisID: 'y'
        },
        {
          type: 'bar',
          backgroundColor: 'rgba(250, 206, 85, .5)',
          borderColor: 'rgba(250, 206, 85, .5)',
          borderWidth: 3,
          label: 'Uso / Aderidos (%)',
          data: this.grownMetricsRows.map(el => el.use / el.adhered).reverse(),
          yAxisID: 'y2'
        },
      ];

      this.chart.update();
    } else {
      this.initChart();
      this.updateChart();
    }
  }

  sumGroups = (groups) => {
    const fieldsToSum = ['registered', 'adhered', 'use', 'potentialEconomy', 'couponLaunchedEconomy', 'cinema_value',
      'coupons', 'launchedEconomy', 'cinema_unique_users', 'cinema_tickets'];

    const groupsSum = groups.reduce((a, b) => {
      const element = {};
      fieldsToSum.forEach(el => {
        element[el] = (a ? (a[el] || 0) : 0) + (b ? (b[el] || 0) : 0);
      });
      return element;
    }, {});

    return {
      ...groupsSum,
      launchedEconomy: (groupsSum.couponLaunchedEconomy + groupsSum.cinema_value) || 0,
      adhered_registered: groupsSum.adhered / groupsSum.registered,
      use_registered: groupsSum.use / groupsSum.registered,
      use_adhered: groupsSum.use / groupsSum.adhered
    };
  };
}
